'use client'

import { tw } from 'utils/classnames'
import { CheckIcon } from 'primitives/icons'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useTranslations } from 'next-intl'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { Typography } from '@repo/design-system-cf/components/typography'
import { Button } from '@repo/design-system-cf/components/button'

import { BannerContainer } from './container'
import { CountdownSection } from '../all-access-yearly-trial'

interface Props {
  banner: Omit<
    NonNullable<SiteDataQuery['siteData']['allAccessYearlyTrialBanner']>,
    '__typename' | 'type'
  > & {
    type: string
  }
  theme?: StudioAiTrialTheme
}

/** Colour schemes for the GROW-3339 top-banner test. Copy and layout are identical to control. */
export type StudioAiTrialTheme = 'navy' | 'mint' | 'lavender'

interface ThemeStyles {
  container: string
  /**
   * Same hue as `container` at partial alpha, laid over the sparkles behind each text column. Being
   * the same hue means it composites to the container colour and leaves no visible edge — it only
   * shows up as the sparkles fading where content sits on top of them.
   */
  contentScrim: string
  heading?: string
  features?: string
  checkIcon?: string
  sparkle: string
  countdownDigits?: string
  countdownLabel?: string
  cancelAnytime: string
}

const THEMES: Record<StudioAiTrialTheme, ThemeStyles> = {
  // No token in the cf palette lands near the mockup's desaturated navy — cf-blue-1000 (#081d59) is
  // far more saturated and cf-grey-900 (#1a1c1f) is neutral — so this one background is a raw hex.
  navy: {
    container: 'bg-[#1e2749]',
    contentScrim: 'bg-[#1e2749]/50',
    heading: 'text-cf-grey-0',
    features: 'text-cf-grey-0',
    // The shared --checkIconColor (#248142) fails contrast on the navy background.
    checkIcon: 'text-cf-action-green-400',
    sparkle: 'text-cf-yellow-400',
    countdownDigits: 'text-cf-red-500',
    countdownLabel: 'text-cf-grey-300',
    cancelAnytime: 'text-cf-grey-300',
  },
  mint: {
    container: 'bg-cf-theme-green-100',
    contentScrim: 'bg-cf-theme-green-100/50',
    sparkle: 'text-cf-theme-green-300',
    cancelAnytime: 'text-cf-grey-600',
  },
  lavender: {
    container: 'bg-cf-purple-50',
    contentScrim: 'bg-cf-purple-50/50',
    heading: 'text-cf-purple-900',
    sparkle: 'text-cf-purple-400',
    cancelAnytime: 'text-cf-grey-600',
  },
}

const buttonStyles =
  'bg-[--bannerButtonColor] hover:-[bannerButtonHoverColor] text-[--bannerButtonTextColor] h-[45px] w-full items-center justify-center rounded py-5 px-2 text-[1rem] font-bold uppercase tracking-[0.9px] text-center leading-[18px]'

// Four-point sparkle, matching the design mock's clip-path rather than an icon-font glyph:
// IconAutoAwesome bakes one large star plus two small ones into a single symbol, so it renders as a
// cluster wherever it is placed instead of the mock's individual stars.
const SPARKLE_CLIP_PATH =
  'polygon(50% 0%, 59% 41%, 100% 50%, 59% 59%, 50% 100%, 41% 59%, 0% 50%, 41% 41%)'

/**
 * Decorative sparkles, drawn before the content so they paint underneath it without needing a
 * z-index. The container's `overflow-hidden` keeps them inside the banner bounds. `bg-current`
 * picks up the theme's sparkle colour from the wrapper's text colour.
 */
function Sparkles({ className }: { className: string }) {
  return (
    <div aria-hidden className={tw('pointer-events-none absolute inset-0', className)}>
      <span
        className="absolute left-[34px] top-[26px] size-[30px] bg-current"
        style={{ clipPath: SPARKLE_CLIP_PATH }}
      />
      <span
        className="absolute left-[70px] top-[96px] size-4 bg-current"
        style={{ clipPath: SPARKLE_CLIP_PATH }}
      />
      <span
        className="absolute right-10 top-5 size-[26px] bg-current"
        style={{ clipPath: SPARKLE_CLIP_PATH }}
      />
    </div>
  )
}

export function StudioAiTrial({ banner, theme }: Props) {
  const t = useTranslations('cf_banners')
  const { features, headline, buttonText } = banner
  const themeStyles = theme ? THEMES[theme] : undefined

  function onButtonClick() {
    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  // The themed arms replace the cream promo artwork with a flat background colour, so the remote
  // image is dropped rather than layered underneath.
  const backgroundImage = themeStyles ? undefined : banner.backgroundImage
  const entryPoint = PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV

  // Control keeps its bespoke `buttonStyles` so the experiment's baseline is untouched; the themed
  // arms use the design system Button instead of restating those styles.
  const renderCta = (className: string) => {
    const trialButton = (
      <StartFreeTrialButton
        onClick={onButtonClick}
        className={themeStyles ? undefined : tw(buttonStyles, className)}
        label={buttonText}
        entryPoint={entryPoint}
        subscriptionType="studio-ai"
      />
    )

    if (!themeStyles) {
      return trialButton
    }

    return (
      <Button
        asChild
        variant="primary-green-bright"
        size="large"
        fullWidth
        className={tw('uppercase', className)}
      >
        {trialButton}
      </Button>
    )
  }

  return (
    <div
      style={{
        '--bannerButtonTextColor': '#FFFFFF',
        '--bannerButtonColor': '#248142',
        '--bannerButtonHoverColor': '#1b6633',
        '--checkIconColor': '#248142',
      }}
    >
      <BannerContainer
        className={themeStyles?.container}
        backgroundImage={backgroundImage}
        wrapperClassName="items-start"
        bannerType={banner.type}
      >
        {themeStyles ? <Sparkles className={themeStyles.sparkle} /> : null}
        <div className={tw('w-full z-0', themeStyles?.contentScrim)}>
          <div
            className={tw(
              'mb-[10px] text-center text-[12px] leading-7 tracking-[0.3px] text-[#2A1F3F] md:text-left md:text-[16px] [&_.u-bold]:text-[16px] [&_.u-bold]:font-bold md:[&_.u-bold]:text-[24px]',
              themeStyles?.heading,
            )}
            dangerouslySetInnerHTML={{ __html: headline }}
          />
          {renderCta('mb-3 flex w-full md:hidden')}
          {features ? (
            <ul className={tw('mt-3.5 hidden md:block', themeStyles?.features)}>
              {features.map((feat, index) => {
                return (
                  <li
                    key={index}
                    className={tw(
                      'my-1 flex items-center leading-[1.618]',
                      index > 0 && 'hidden md:flex',
                    )}
                  >
                    <CheckIcon
                      className={tw('mr-1.5 text-[--checkIconColor]', themeStyles?.checkIcon)}
                    />
                    <span dangerouslySetInnerHTML={{ __html: feat }} />
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        <div
          className={tw(
            'hidden w-[47%] flex-col items-center md:flex',
            themeStyles?.features,
            themeStyles?.contentScrim,
          )}
        >
          <CountdownSection
            freeTodayText={t('try_free_today')}
            hoursText={t('hours')}
            minutesText={t('mins')}
            secondsText={t('secs')}
            digitsClassName={themeStyles?.countdownDigits}
            labelClassName={themeStyles?.countdownLabel}
          />
          {renderCta('hidden md:flex')}
          <Typography
            variant="cf-label-02"
            className={tw('mt-2 text-semantic-foreground-color-subtle', themeStyles?.cancelAnytime)}
          >
            {t('cancel_anytime')}
          </Typography>
        </div>
      </BannerContainer>
    </div>
  )
}
